/* eslint-disable no-unused-vars, array-bracket-newline */
import { useCallback } from 'react';
import ReactTooltip from 'react-tooltip';
import { IDexTrade, enums } from '@kattana-io/common';
import DesktopTable, {
  ITablePopup,
} from '../../../molecules/Table/DesktopTable';
import Text from '../../../atoms/Text';
import Stats from '../Stats';
import { ReactComponent as RobotIcon } from '../images/robot.svg';
import { formatTimestamp, formatTx, toFixed4 } from '../formatters';
import { useTableData } from './useTableData';
import { onTxScan, onWalletScan } from '../../../helpers/addressUtils';
import { TableTrade } from '../../../core/models/table-trade.model';
import NumberDisplay from '../../../molecules/NumberDisplay';
import WalletField from '../../../molecules/WalletField';

export const LS_TRADES_HIDDEN_COLUMNS_KEY =
  'kattana-simple.trades_table_columns_hidden';
export const LS_TRADES_KEY = 'kattana-simple.trades_table_columns';

const getTradesToDownload = (plan: enums.PricingPlan, trades: TableTrade[]) => {
  if (plan === enums.PricingPlan.premium) {
    return trades;
  }

  const ts = Math.floor(new Date().getTime() / 1000);
  const tsYesterday = ts - 24 * 3600;

  const yesterdayTradeIdx = trades.findIndex(
    (trade) => trade.timestamp - tsYesterday < 0
  );

  if (yesterdayTradeIdx > 100 || yesterdayTradeIdx === -1) {
    return trades.slice(0, 100);
  }
  return trades.slice(0, yesterdayTradeIdx);
};

interface ITradesTableProps {
  flipped: boolean;
  trades: TableTrade[];
  timezone?: string;
  base: string;
  quote: string;
  address: string | undefined;
  chain: string;
  plan: enums.PricingPlan;
  loading: boolean;
  currentWallet?: string;
  width?: number;
  date?: number;
  tablePopup?: ITablePopup;
  onTradeClick(trade: IDexTrade): void;
  openWalletModal(props: any): void;
  onScroll?(listHeight: number, scrollPos: number, isInitialPos: boolean): void;
}

const sellClassName = 'row--Sell text--red-600';
const buyClassName = 'row--Buy text--green-600';
const myTrade = 'row--Active';

/**
 * Implements a table with trades
 * @param param0
 */
const TradesTable = ({
  currentWallet,
  trades,
  timezone,
  base,
  quote,
  loading,
  flipped,
  address,
  width,
  chain,
  plan,
  date,
  tablePopup,
  onTradeClick,
  openWalletModal,
  onScroll,
}: ITradesTableProps) => {
  const showModal = ({ wallet }: TableTrade) =>
    openWalletModal({
      wallet: wallet,
      address,
      chain,
      timeframe: 'latest',
      date,
      timezone,
    });

  const notUSDPrice = !quote.includes('USD');

  const { columns, data, tableColumnsWidth } = useTableData({
    trades,
    quote: flipped ? base : quote,
  });

  const headers = ['Date;Action;Price ($);Price;Value;Wallet']; // Headers
  const csvData = [
    ...headers,
    ...getTradesToDownload(plan, trades).map(
      (trade: any) =>
        `${formatTimestamp(trade.timestamp, timezone)};${trade.side};${toFixed4(
          trade.priceUSD
        )};${toFixed4(trade.price)};${toFixed4(trade.txValue)};${trade.wallet}`
    ),
  ];

  const renderCell = (cell: any) => {
    switch (cell.column.id) {
    case 'date':
      return <Text size={12}>{formatTimestamp(cell.value, timezone)}</Text>;
    case 'numericIn':
      return (
        <NumberDisplay
          value={cell.value}
          endSymbol={` ${base}`}
          textProps={{ size: 12 }}
        />
      );
    case 'numericOut':
      return (
        <NumberDisplay
          value={cell.value}
          endSymbol={` ${quote}`}
          textProps={{ size: 12 }}
        />
      );
    case 'price':
      return (
        <NumberDisplay
          value={cell.value}
          textProps={{ size: 12, className: notUSDPrice ? '' : 'price' }}
        />
      );
    case 'priceUSD':
      return (
        <NumberDisplay
          value={cell.value}
          textProps={{ size: 12, className: 'price' }}
        />
      );
    case 'txValue':
      return (
        <Text size={12}>
          {cell.row.original.sandwich && (
            <>
              <RobotIcon
                data-for={`sandwich-tooltip-${cell.row.id}`}
                data-tip={`Revenue : ${Math.abs(
                  cell.row.original.sandwichAmount
                ).toFixed(5)} ${flipped ? base : quote}`}
              />
              &nbsp;&nbsp;
              <ReactTooltip
                id={`sandwich-tooltip-${cell.row.id}`}
                place='left'
                effect='solid'
                overridePosition={({ left }) => {
                  return {
                    left: left,
                    top: -2,
                  };
                }}
                className='actions__tooltip text_tooltip'
              />
            </>
          )}
          <NumberDisplay value={cell.value} string />
        </Text>
      );
    case 'statistics':
      return (
        <Stats
          data={cell.row.original}
          address={address}
          chain={chain}
          date={date}
          timezone={timezone}
          onShowModal={() => {
            showModal(cell.row.original);
          }}
        />
      );
    case 'wallet':
      return (
        <WalletField
          textProps={{
            size: 12,
            className: 'tx',
            onClick: () => onWalletScan(cell.value, chain),
          }}
          wallet={cell.value}
          network={chain}
        />
      );
    case 'tx':
      return (
        <div className='tx' onClick={() => onTxScan(cell.value, chain)}>
          <Text size={12}>{formatTx(cell.value)}</Text>
        </div>
      );
    default:
      return <Text size={12}>{cell.value}</Text>;
    }
  };

  /**
   * acknowledge flipped when displaying buy/sell
   */
  const getClassNameForRow = useCallback(
    (row: any) => {
      const classNames: string[] = [];
      if (row.wallet === currentWallet) {
        classNames.push(myTrade);
      }
      if (row.side === 'Buy') {
        if (flipped) {
          classNames.push(sellClassName);
        } else {
          classNames.push(buyClassName);
        }
      } else {
        if (flipped) {
          classNames.push(buyClassName);
        } else {
          classNames.push(sellClassName);
        }
      }
      return classNames.join(' ');
    },
    [flipped, currentWallet]
  );

  return (
    <DesktopTable
      hiddenColumnsKey={LS_TRADES_HIDDEN_COLUMNS_KEY}
      dataKey={LS_TRADES_KEY}
      title='trades'
      tableColumnsWidth={tableColumnsWidth}
      tableWidth={width || 0}
      columns={columns}
      defaultSortedColumn={{ id: 'date', desc: true }}
      data={data}
      csvData={csvData}
      loading={loading}
      hiddenColumns={['blocknumber', 'txString', 'order']}
      hoveredKey='wallet'
      popup={tablePopup}
      getClassNameForRow={getClassNameForRow}
      renderCell={renderCell}
      onRowClick={onTradeClick}
      onScroll={onScroll}
    />
  );
};

export default TradesTable;
